import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth, API } from '../context/AuthContext';
import axios from 'axios';
import {
  MagnifyingGlass,
  Funnel,
  User,
  CalendarBlank,
  ArrowRight,
  ClockCounterClockwise
} from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const actionLabels = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  publish: 'Published',
  unpublish: 'Unpublished',
  archive: 'Archived',
  approve: 'Approved',
  reject: 'Rejected',
  export: 'Exported',
  duplicate: 'Duplicated',
  submit_for_review: 'Submitted for Review'
};

const entityLabels = {
  campaign: 'Campaign',
  department: 'Department',
  user: 'User',
  domain: 'Domain',
  template: 'Template',
  submission: 'Submission',
  submissions: 'Submissions'
};

export function AuditLogsPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const params = {};
        if (entityFilter !== 'all') params.entity_type = entityFilter;
        if (actionFilter !== 'all') params.action = actionFilter;
        
        const { data } = await axios.get(`${API}/audit-logs`, { params, withCredentials: true });
        setLogs(data);
      } catch (error) {
        console.error('Failed to fetch audit logs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [entityFilter, actionFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'bg-emerald-100 text-emerald-700',
      update: 'bg-blue-100 text-blue-700',
      delete: 'bg-red-100 text-red-700',
      publish: 'bg-green-100 text-green-700',
      unpublish: 'bg-amber-100 text-amber-700',
      archive: 'bg-zinc-100 text-zinc-700',
      approve: 'bg-emerald-100 text-emerald-700',
      reject: 'bg-red-100 text-red-700',
      export: 'bg-purple-100 text-purple-700'
    };
    return colors[action] || 'bg-zinc-100 text-zinc-700';
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-black tracking-tight text-zinc-900">Audit Logs</h1>
          <p className="text-zinc-500 mt-1">Track all system activities and changes</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-[180px]" data-testid="entity-filter">
              <SelectValue placeholder="Filter by entity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              {Object.entries(entityLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[180px]" data-testid="action-filter">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {Object.entries(actionLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Logs List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-20 bg-zinc-100 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : logs.length > 0 ? (
          <div className="space-y-3">
            {logs.map((log) => (
              <div
                key={log.id}
                className="bg-white border border-zinc-200 rounded-sm p-4 hover:border-zinc-300 transition-colors"
                data-testid={`audit-log-${log.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-zinc-100 rounded-sm flex items-center justify-center flex-shrink-0">
                      <ClockCounterClockwise className="w-5 h-5 text-zinc-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`${getActionColor(log.action)} px-2 py-0.5 rounded-sm text-xs font-medium`}>
                          {actionLabels[log.action] || log.action}
                        </span>
                        <span className="text-zinc-400">→</span>
                        <span className="font-medium text-zinc-900">
                          {entityLabels[log.entity_type] || log.entity_type}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-500 mt-1">
                        by <span className="font-medium">{log.user_email || 'Unknown'}</span>
                      </p>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-2 text-xs text-zinc-500 font-mono bg-zinc-50 p-2 rounded-sm">
                          {JSON.stringify(log.details, null, 2).substring(0, 200)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-zinc-500 whitespace-nowrap">
                    {formatDate(log.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white border border-zinc-200 rounded-sm">
            <ClockCounterClockwise className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No audit logs found</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
